import "./text-input.scss";
import { Fragment } from "react";
import { useState, useEffect, useContext, FocusEvent  } from "react";
import { Form } from "react-bootstrap";
import { RxCross2 } from "react-icons/rx";

import Tooltip from "../../../shared/components/tooltip/tooltip";
import { roleRead } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import { createUUID } from "../../../shared/utils/utils";

export interface InputConfig {
  type: string;
  id: string;
  label?: string;
  required: boolean;
  inputKey: string;
  disabled?: boolean;
  subType?: string;
  mode?: string;
  controlled?: boolean;
  value?: string | number;
  inputTypeLimit?: number;
  onlyWholeNumbers?: boolean;
  placeholder?: string;
  autofocus?: boolean;
  defaultValue?: string;
  toggleWarning?: boolean;
  noRoleCheck?: boolean;
  errorMsg?: string;
  extraToolTipInfo?: string;
  name?: string;
  decimalsFormatting?: number;
  allowedByParent?: boolean;
  extraId?: string;
  generateUniqueId?: boolean;
  extraClass?: string;
  defineStep?: () => number | undefined;
  deleteInput?: (id: string) => void;
  handleOnChange(newValue: string, key?: string, id?: string, extraId?: string): void;
  handleOnFocus?(): void;
  handleOnBlur?(e:FocusEvent): void; 
  onKeyDown?(event:React.KeyboardEvent): void;
}

const TextInput = ({
  type,
  id,
  label,
  name,
  required,
  inputKey,
  disabled,
  subType,
  mode,
  inputTypeLimit,
  controlled,
  value,
  onlyWholeNumbers,
  placeholder,
  autofocus,
  defaultValue,
  toggleWarning,
  allowedByParent,
  decimalsFormatting,
  errorMsg,
  extraClass,
  noRoleCheck,
  generateUniqueId,
  extraId,
  extraToolTipInfo,
  defineStep,
  deleteInput,
  onKeyDown,
  handleOnChange,
  handleOnFocus,
  handleOnBlur,
}: InputConfig) => {
  const [readOnlyState, setReadOnlyState] = useState(false);
  const authCtx = useContext(AuthContext);

  useEffect(() => {
    if (authCtx.selectedTenant.roles) {
      const rolesArray = authCtx.selectedTenant.roles;
      if (allowedByParent) {
        setReadOnlyState(false);
      }
      else if (rolesArray.length <= 1 && rolesArray.indexOf(roleRead) !== -1 && !noRoleCheck){
        setReadOnlyState(true);
      } else {
        setReadOnlyState(false);
      }
    }
  }, [authCtx.selectedTenant.roles]);

  const onChangeStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleOnChange(e.target.value, inputKey, id, extraId);
  };

  const checkTypeAndLimit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (onlyWholeNumbers) {
      if (!(/^[0-9\b]+$/.test(e.key))) {
        e.preventDefault();
      }
    }
    if (!inputTypeLimit) {
      return;
    }

    if (/^[0-9,\b]+$/.test(e.key)) {
      // test if the input key is = 0-9 or "," 
      let newCurrentValue;
      if (target.value && Number(target.value) === 0) {
        newCurrentValue = Number(`${Number(target.value)}.${e.key}`);
      } else {
        newCurrentValue = Number(`${target.value}${e.key}`);
      }
      // check max
      if (inputTypeLimit && inputTypeLimit < newCurrentValue) {
        e.preventDefault();
      }
      // check length
      if (decimalsFormatting) {
        if (newCurrentValue.toString().length > decimalsFormatting.toString().length + 1) {
          e.preventDefault();
        }
      }
    } else {
      e.preventDefault();
    }
  };

  return (
    <div className={`textInput ${extraClass}`}>
      <Form.Group
        className="textInput-group has-validation"
        controlId={generateUniqueId? createUUID() : id}
      >
        {label? <span className={mode === "aside" ? "textInput-aside-label" : ""}>
          <Form.Label>
            {label}
            {required ? (
              " *"
            ) : (
              ""
            )}
            {extraToolTipInfo? <span className="textInput-infoIcon"><Tooltip content={extraToolTipInfo} direction="top" delay={350}  /></span> : ""}
            {deleteInput && <span className="textInput-deleteInput" onClick={() => deleteInput(id)}>
              <RxCross2 />
            </span>}
          </Form.Label>
        </span> : ""}
        {controlled ? (
          <Fragment>
            <Form.Control
              className={
                (toggleWarning ? "is-invalid" : "") +
                (mode === "aside" ? "textInput-aside" : "")
              }        
              type={subType? subType : type}
              as={type === "textarea" ? "textarea" : undefined}
              disabled={readOnlyState || disabled}
              placeholder={placeholder}
              rows={type === "textarea" ? 3 : undefined}
              required={required}
              autoFocus={autofocus}
              value={value === null || value === undefined ? "" : value}
              max={defineStep ? 1 : undefined}
              min={defineStep? 0 : undefined}
              step={type === "number" && defineStep? defineStep() :  undefined}
              onChange={onChangeStatus}
              onKeyDown={(event) => onKeyDown && onKeyDown(event)}
              onBlur={(e) => handleOnBlur && handleOnBlur(e)}
              onFocus={() => handleOnFocus && handleOnFocus()}
              autoComplete="off"
              readOnly={readOnlyState}
              onKeyPress={checkTypeAndLimit}
              name={name}
            />
          </Fragment>
        ) : (
          <Fragment>
            <Form.Control
              type={subType? subType : type}
              className={
                (toggleWarning ? "is-invalid" : "") +
                (mode === "aside" ? "textInput-aside" : "")
              }
              placeholder={placeholder}
              required={required}
              autoFocus={autofocus}
              as={type === "textarea" ? "textarea" : undefined}
              rows={type === "textarea" ? 3 : undefined}
              max={defineStep ? 1 : undefined}
              min={defineStep? 0 : undefined}
              step={type === "number" && defineStep? defineStep() :  undefined}
              disabled={readOnlyState || disabled}
              defaultValue={defaultValue}
              onChange={onChangeStatus}
              onBlur={(e) => handleOnBlur && handleOnBlur(e)}
              onKeyDown={(event) => onKeyDown && onKeyDown(event)}
              onFocus={() => handleOnFocus && handleOnFocus()}
              autoComplete="off"
              readOnly={readOnlyState}
              name={name}
              onKeyPress={checkTypeAndLimit}
            />
          </Fragment>
        )}

        <Form.Control.Feedback type="invalid">
          {errorMsg? errorMsg : `Please provide the ${label || "" }`}
        </Form.Control.Feedback>
      </Form.Group>
    </div>
  );
};

export default TextInput;